import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { formatNumber, formatPercent } from '../lib/utils';
import { LineItemTable } from './LineItemTable';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useMasterPriceListColumnConfig } from '../hooks/useColumnConfig';
import { createMasterPriceListRowQueue } from './masterPriceListRowQueue';

const CURRENCIES = ['CHF', 'EUR', 'USD'];

const DEFAULT_ITEM = {
  name: 'Untitled',
  category: 'General',
  qty: 1,
  revenue_unit: 0,
  revenue_currency: 'CHF',
  cost_unit: 0,
  cost_currency: 'CHF',
  vendor: '',
  discount_type: 'none',
  discount_value: 0,
  description_plain: '',
  description_html: '',
  internal_notes: '',
  tags: [],
  sort_order: 0,
  is_active: true,
};

function masterItemToLineItemShape(item) {
  return {
    ...item,
    title: item.title || item.name,
    name: item.name || item.title || 'Untitled',
  };
}

function lineItemPatchToMasterPayload(item) {
  return {
    name: String(item.name || item.title || 'Untitled').trim() || 'Untitled',
    category: item.category || '',
    qty: Number(item.qty) || 0,
    revenue_unit: Number(item.revenue_unit) || 0,
    revenue_currency: item.revenue_currency || 'CHF',
    cost_unit: Number(item.cost_unit) || 0,
    cost_currency: item.cost_currency || 'CHF',
    vendor: item.vendor || '',
    discount_type: item.discount_type || 'none',
    discount_value: Number(item.discount_value) || 0,
    description_plain: item.description_plain || '',
    description_html: item.description_html || '',
    internal_notes: item.internal_notes || '',
    tags: item.tags || [],
    sort_order: Number(item.sort_order) || 0,
    is_active: item.is_active !== false,
  };
}

export function MasterPriceListTab({
  dealId,
  categories,
  vendors,
  fxRates,
  onCreateCategory,
  onDeleteCategory,
  onCreateVendor,
  onImported,
}) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    vendor: 'all',
    currency: 'all',
    state: 'active',
  });
  const columnConfig = useMasterPriceListColumnConfig();
  const itemsRef = useRef([]);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const rowSavesInFlightRef = useRef(0);

  const replaceItems = useCallback((nextItems) => {
    itemsRef.current = nextItems;
    setItems(nextItems);
  }, []);

  const rowQueueRef = useRef(null);
  if (rowQueueRef.current == null) {
    rowQueueRef.current = createMasterPriceListRowQueue({
      readRow: (itemId) => itemsRef.current.find((it) => it.id === itemId) || null,
      writeRow: (itemId, next) => {
        replaceItems(
          itemsRef.current.map((it) => (it.id === itemId ? masterItemToLineItemShape(next) : it)),
        );
      },
      saveRow: (itemId, row) => api.updateMasterPriceListItem(itemId, lineItemPatchToMasterPayload(row)),
      refreshRow: async (itemId) => {
        const res = await api.getMasterPriceListItem(itemId);
        const summaryRes = await api.getMasterPriceListSummary({ state: filtersRef.current.state });
        setSummary(summaryRes.data || null);
        return masterItemToLineItemShape(res.data);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.detail || 'Failed to update item');
      },
    });
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, summaryRes] = await Promise.all([
        api.getMasterPriceListItems(filters),
        api.getMasterPriceListSummary({ state: filters.state }),
      ]);
      replaceItems((itemsRes.data || []).map(masterItemToLineItemShape));
      setSummary(summaryRes.data || null);
    } catch (error) {
      console.error('Failed to load master price list:', error);
      toast.error(error?.response?.data?.detail || 'Failed to load master price list');
    } finally {
      setLoading(false);
    }
  }, [filters, replaceItems]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.size) return prev;
      const visibleIds = new Set(items.map((it) => it.id));
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  const categoryOptions = useMemo(() => {
    const fromItems = items.map((it) => String(it.category || '').trim()).filter(Boolean);
    const fromCatalog = (categories || []).map((c) => c.name).filter(Boolean);
    return [...new Set([...fromCatalog, ...fromItems])].sort((a, b) => a.localeCompare(b));
  }, [categories, items]);

  const vendorOptions = useMemo(() => {
    const fromItems = items.map((it) => String(it.vendor || '').trim()).filter(Boolean);
    const fromCatalog = (vendors || []).map((v) => v.name).filter(Boolean);
    return [...new Set([...fromCatalog, ...fromItems])].sort((a, b) => a.localeCompare(b));
  }, [vendors, items]);

  const addItem = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.createMasterPriceListItem(DEFAULT_ITEM);
      replaceItems([...itemsRef.current, masterItemToLineItemShape(res.data)]);
      const summaryRes = await api.getMasterPriceListSummary({ state: filters.state });
      setSummary(summaryRes.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create item');
    } finally {
      setLoading(false);
    }
  }, [filters.state, replaceItems]);

  const updateItem = useCallback((itemId, patch) => {
    const job = rowQueueRef.current.apply(itemId, patch);
    if (!job) return undefined;
    rowSavesInFlightRef.current += 1;
    setLoading(true);
    return job.finally(() => {
      rowSavesInFlightRef.current -= 1;
      if (rowSavesInFlightRef.current === 0) setLoading(false);
    });
  }, []);

  const deleteItem = useCallback(async (itemId) => {
    const current = itemsRef.current.find((it) => it.id === itemId);
    if (!current) return;
    const deactivate = current.is_active !== false;
    const msg = deactivate
      ? 'Deactivate this master price list item? It will be hidden from import lists.'
      : 'Permanently delete this master price list item?';
    if (!window.confirm(msg)) return;
    setLoading(true);
    try {
      if (deactivate) {
        await api.updateMasterPriceListItem(itemId, lineItemPatchToMasterPayload({ ...current, is_active: false }));
      } else {
        await api.deleteMasterPriceListItem(itemId);
      }
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to remove item');
    } finally {
      setLoading(false);
    }
  }, [loadData]);

  const duplicateItem = useCallback(async (itemId) => {
    setLoading(true);
    try {
      await api.duplicateMasterPriceListItem(itemId);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to duplicate item');
    } finally {
      setLoading(false);
    }
  }, [loadData]);

  const toggleSelect = useCallback((itemId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((checked) => {
    const importable = items.filter((it) => it.is_active !== false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        importable.forEach((it) => next.add(it.id));
      } else {
        importable.forEach((it) => next.delete(it.id));
      }
      return next;
    });
  }, [items]);

  const importSelected = useCallback(async () => {
    const ids = items
      .filter((it) => it.is_active !== false && selectedIds.has(it.id))
      .map((it) => it.id);
    if (!ids.length || !dealId) return;
    setLoading(true);
    try {
      const res = await api.importMasterPriceListToDeal(dealId, { item_ids: ids });
      const n = ids.length;
      toast.success(`${n} catalog item${n === 1 ? '' : 's'} imported successfully`);
      setSelectedIds(new Set());
      onImported?.(res.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to import selected items');
    } finally {
      setLoading(false);
    }
  }, [items, selectedIds, dealId, onImported]);

  const restoreItem = useCallback(async (itemId) => {
    const current = itemsRef.current.find((it) => it.id === itemId);
    if (!current) return;
    setLoading(true);
    try {
      await api.updateMasterPriceListItem(
        itemId,
        lineItemPatchToMasterPayload({ ...current, is_active: true }),
      );
      await loadData();
      toast.success('Item reactivated');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to reactivate item');
    } finally {
      setLoading(false);
    }
  }, [loadData]);

  const totals = summary
    ? {
        total_revenue_chf: summary.total_revenue_chf,
        total_cost_chf: summary.total_cost_chf,
        total_margin_chf: summary.total_margin_chf,
        total_margin_pct: summary.average_margin_pct,
      }
    : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white" data-testid="master-price-list-tab">
      <div className="shrink-0 border-b border-gray-200 px-4 py-3 lg:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="pl-page-title">Master Price List</h1>
            <p className="mt-1 text-xs text-gray-500">
              Reusable EvoHome service pricing — select catalog rows and import into the open deal without re-entering standard lines.
            </p>
            {!dealId ? (
              <p className="mt-1 text-[11px] text-amber-700">
                Open a deal to enable multi-select import into line items.
              </p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 lg:gap-2">
            <Input
              placeholder="Search name, category, vendor…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              className="col-span-2 h-9 text-xs sm:col-span-3 lg:col-span-1"
              data-testid="master-price-list-search"
            />
            <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.vendor} onValueChange={(v) => setFilters((f) => ({ ...f, vendor: v }))}>
              <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Vendor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vendors</SelectItem>
                {vendorOptions.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.currency} onValueChange={(v) => setFilters((f) => ({ ...f, currency: v }))}>
              <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Currency" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All currencies</SelectItem>
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.state} onValueChange={(v) => setFilters((f) => ({ ...f, state: v }))}>
              <SelectTrigger className="h-9 text-xs" data-testid="master-price-list-state-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <LineItemTable
            title="Catalog items"
            addRowLabel="Add item"
            emptyMessage='No master price list items yet. Click "Add item" to define a standard service line.'
            items={items}
            categories={categories}
            vendors={vendors}
            onAddItem={addItem}
            onUpdateItem={updateItem}
            onDeleteItem={deleteItem}
            onDuplicateItem={duplicateItem}
            onRestoreItem={restoreItem}
            onCreateCategory={onCreateCategory}
            onDeleteCategory={onDeleteCategory}
            onCreateVendor={onCreateVendor}
            loading={loading}
            marginTarget={30}
            presentationCurrency="CHF"
            vendorCurrency="CHF"
            coreCurrency="CHF"
            fxRates={fxRates}
            dealTotals={totals}
            columnConfig={columnConfig}
            nameLayout="catalog"
            selectable={Boolean(dealId)}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
            onImportSelected={dealId ? importSelected : undefined}
            importSelectedLabel="Import Selected Items"
          />
        </div>

        <aside className="shrink-0 overflow-y-auto border-t border-gray-200 bg-slate-50 p-4 lg:w-[260px] lg:border-l lg:border-t-0" data-testid="master-price-list-summary">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Summary</h2>
          <dl className="mt-3 space-y-3">
            <div>
              <dt className="text-xs text-gray-500">Total revenue</dt>
              <dd className="text-lg font-semibold tabular-nums text-gray-900">{formatNumber(summary?.total_revenue_chf ?? 0)} CHF</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Total cost</dt>
              <dd className="text-lg font-semibold tabular-nums text-gray-900">{formatNumber(summary?.total_cost_chf ?? 0)} CHF</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Total margin</dt>
              <dd className="text-lg font-semibold tabular-nums text-emerald-700">{formatNumber(summary?.total_margin_chf ?? 0)} CHF</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Average margin %</dt>
              <dd className="text-lg font-semibold tabular-nums text-gray-900">{formatPercent(summary?.average_margin_pct ?? 0)}</dd>
            </div>
            <div className="border-t border-gray-200 pt-3 text-xs text-gray-500">
              {summary?.item_count ?? 0} item{(summary?.item_count ?? 0) === 1 ? '' : 's'}
              {filters.state !== 'inactive' ? ` · ${summary?.active_count ?? 0} active` : ''}
            </div>
          </dl>
        </aside>
      </div>
    </div>
  );
}
